// Vue
import { ref, watch } from "vue";

// Dependencies
import { defineStore } from "pinia";
import { useRouter } from "vue-router";
// Features
import { ModelType, services as BuildingProjectServices } from "@/features/BuildingProject";

// Types
import { ProjectWrapperType } from "../types/ProjectWrapperType";
import { toProjectWrapper } from "../services/projectWrapperFromHasura";
import { TransportTypesType } from "@/features/BuildingProject";
import { ProjectAdditionalInfoWrapperType } from "../types/ProjectAdditionalInfoWrapperType";

// Utils
import { getBuildingProjectById } from "@/api/services/BuildingProject";
import { getProjectPermissions } from "@/providers/Permissions/getProjectPermissions";

import gaiupApolloClient from "@/graphql/services/ApolloClient";

import { useLocalization } from "@/providers/Localization";

// GraphQL
import {
  InsertProjectTransportDocument,
  InsertProjectTransportMutation,
  InsertProjectTransportMutationVariables,
  InsertProjectTransportWithVersionDocument,
  InsertProjectTransportWithVersionMutation,
  InsertProjectTransportWithVersionMutationVariables,
  Transport_Type_Enum,
  GetProjectDocument,
  Project_Transport_Default_Constraint,
  Project_Transport_Default_Update_Column,
  GetProjectQuery,
  GetProjectQueryVariables,
  GetProjectSettingsDocument,
  GetProjectSettingsQuery,
  GetProjectSettingsQueryVariables,
} from "@/graphql";

/** "rest" is `GET api/BuildingProject/{id}` (Hasura + Marten materials); "hasura" is GetProjectSettings,
 * the same wrapper shape without materials, for surfaces that never show them. */
export type ProjectReadSource = "rest" | "hasura";
import { isVersioningSchemaMissing } from "@/pages/ProjectVersionHistory/services/versioningSchema";
import { workingRevisionFilter } from "@/pages/ProjectVersionHistory/services/revisionScope";
import { toTransportTypes } from "../services/transportTypesFromHasura";

interface ProjectWrapperProvider {
  getProjectWrapper: () => ProjectWrapperType;
  getProjectAdditionalInfoWrapper: () => ProjectAdditionalInfoWrapperType;
  getTransportTypes: () => TransportTypesType[];
  getAddedTransportTypeIds: () => string[];
  setAddedTransportTypeIds: (newIds: string[]) => void;
  resetEmissionFactors: () => void;
  insertProjectTransport: (transportTypesToSave?: TransportTypesType[], projectId?: string) => Promise<void>;
  fetchProject: (id: string) => Promise<ProjectWrapperType>;
  fetchProjectFromHasura: (id: string) => Promise<ProjectWrapperType>;
  refreshProjectWrapper: (id: string, force?: boolean, source?: ProjectReadSource) => Promise<void>;
  refreshProjectAdditionalInfoWrapper: (id: string, force?: boolean) => Promise<void>;
  forceUpdateProjectWrapperAndAdditionalInfo: (id: string, source?: ProjectReadSource) => Promise<void>;
  reset: () => void;

  // Project state conditions
  hasPhaseA4: () => boolean;
  hasPhaseA5: () => boolean;
  hasPhaseB6: () => boolean;

  // Utility
  getLoading: () => boolean;
  getHasLoaded: () => boolean;
  getError: () => boolean;
}

const useProjectWrapper = defineStore("ProjectWrapperProvider", (): ProjectWrapperProvider => {
  // Refs

  const localization = useLocalization();

  const currentProjectId = ref<string | undefined>(undefined);
  const currentProjectAdditionalInfoId = ref<string | undefined>(undefined);
  // Working-state filter only (`workingRevisionFilter`) — a previewed revision's rows never come through
  // here; they're overlaid separately via `readTransportsFor` so Save can't pick them up.
  const queryVariables = ref<GetProjectQueryVariables>({
    project_id: undefined,
    revisionFilter: workingRevisionFilter(),
    defaultRevisionFilter: workingRevisionFilter(),
  });
  const router = useRouter();

  const projectWrapper = ref<ProjectWrapperType>({
    project: {} as ProjectWrapperType["project"],
    models: [] as ProjectWrapperType["models"],
  });

  const projectAdditionalInfoWrapper = ref<ProjectAdditionalInfoWrapperType>({
    userPermissions: [] as ProjectAdditionalInfoWrapperType["userPermissions"],
  });

  const addedTransportTypeIds = ref<string[]>([]);

  // Getters

  const getProjectWrapper = () => {
    return projectWrapper.value;
  };

  const getProjectAdditionalInfoWrapper = () => {
    return projectAdditionalInfoWrapper.value;
  };

  const getCurrentProjectId = () => {
    return currentProjectId.value;
  };

  const getCurrentProjectAdditionalInfoId = () => {
    return currentProjectAdditionalInfoId.value;
  };

  const getTransportTypes = () => {
    return projectWrapper.value.project.transportTypes;
  };

  const getAddedTransportTypeIds = () => {
    return addedTransportTypeIds.value;
  };

  // Setters

  const setProjectWrapper = (newProjectWrapper: ProjectWrapperType) => {
    projectWrapper.value = newProjectWrapper;
  };

  const setProjectAdditionalInfoWrapper = (newProjectAdditionalInfoWrapper: ProjectAdditionalInfoWrapperType) => {
    projectAdditionalInfoWrapper.value = newProjectAdditionalInfoWrapper;
  };

  const setCurrentProjectId = (newProjectId: string | undefined) => {
    currentProjectId.value = newProjectId;
  };

  const setCurrentProjectAdditionalInfoId = (newProjectId: string | undefined) => {
    currentProjectAdditionalInfoId.value = newProjectId;
  };

  function setQueryVariables(newProject: string | undefined) {
    queryVariables.value = {
      project_id: newProject,
      revisionFilter: workingRevisionFilter(),
      defaultRevisionFilter: workingRevisionFilter(),
    };
  }

  function setAddedTransportTypeIds(newIds: string[]) {
    addedTransportTypeIds.value = newIds;
  }

  // Methods

  /** Callers pass their own edit list — a background refresh can replace `projectWrapper` mid-edit. */
  async function insertProjectTransport(transportTypesToSave?: TransportTypesType[], projectId?: string) {
    const transportTypes = transportTypesToSave ?? projectWrapper.value.project.transportTypes;
    const targetProjectId = projectId ?? getProjectWrapper().project.id;

    // Nothing to write. (The permission webhook cannot validate an empty insert either.)
    if (!transportTypes || transportTypes.length === 0) return;

    // A row matching on (id, project_id) keeps the revision it already has — upserts never move it.
    // isDefault is set by toTransportTypes/Transport.vue only, never a generic row's own flag.
    const objects = transportTypes.map((transportType: TransportTypesType) => {
      const defaultType = transportType.transport.isDefault
        ? {
            default: {
              data: [
                {
                  project_id: targetProjectId,
                },
              ],
              on_conflict: {
                constraint: Project_Transport_Default_Constraint.project_transport_default_project_id_key,
                update_columns: [Project_Transport_Default_Update_Column.project_transport_id],
              },
            },
          }
        : {};

      return {
        ...defaultType,
        id: transportType.id,
        transportId: transportType.transport.id,
        distance: transportType.distance,
        emission: transportType.transport.transportType === Transport_Type_Enum.Custom ? transportType.emission : null,
        project_id: targetProjectId,
        transport_type_name: transportType.transport_type_name,
      };
    });
    const transportTypesData: InsertProjectTransportMutationVariables = { objects };

    // Throws if Hasura confirms fewer rows than sent — a silent skip (e.g. a refused frozen-revision
    // write) must fail the save, not appear to succeed.
    const assertAllWritten = (returning: ReadonlyArray<unknown> | null | undefined) => {
      const written = returning?.length ?? 0;
      if (written !== objects.length) {
        throw new Error(`Transport types: Hasura confirmed ${written} of ${objects.length} rows`);
      }
    };

    // gaiupApolloClient directly, not the Vue Apollo composable: provideApolloClient()/withApollo() only
    // sets the client synchronously, so the context is torn down before an awaited mutate() actually runs.
    try {
      const result = await gaiupApolloClient.mutate<
        InsertProjectTransportWithVersionMutation,
        InsertProjectTransportWithVersionMutationVariables
      >({
        mutation: InsertProjectTransportWithVersionDocument,
        variables: {
          objects,
          version: {
            projectId: targetProjectId,
            entityType: "transport",
            operation: "update",
            summary: "Transport updated",
            changes: [],
          },
        },
        fetchPolicy: "no-cache",
      });
      assertAllWritten(result.data?.insert_project_transport?.returning);
    } catch (e) {
      if (!isVersioningSchemaMissing(e)) throw e;
      const result = await gaiupApolloClient.mutate<InsertProjectTransportMutation, InsertProjectTransportMutationVariables>({
        mutation: InsertProjectTransportDocument,
        variables: transportTypesData,
        fetchPolicy: "no-cache",
      });
      assertAllWritten(result.data?.insert_project_transport?.returning);
    }
  }

  function resetEmissionFactors() {
    // Optional chaining: the create flow calls this before a project is loaded, so reading straight
    // through throws on the first commissioning-year change during creation.
    const energyData = projectWrapper.value?.project?.emissionCalculationAssumptions?.projectEnergyData;
    if (!energyData) return;

    energyData.constructional = {
      heat: null,
      electricity: null,
      earthMoved: null,
      machineryFuel: null,
    };
    energyData.operational = {
      heat: null,
      electricity: null,
    };
  }

  // Methods for fetching project data etc.

  const fetchProject = async (id: string) => {
    // Start loading
    fetchProjectLoading.value = true;

    // Define wrapper
    const wrapper: ProjectWrapperType = {
      project: {} as ProjectWrapperType["project"],
      models: [] as ProjectWrapperType["models"],
    };

    // Get project from params
    const params = await getBuildingProjectById(id);

    if (params?.status) {
      fetchProjectLoading.value = false;

      if (router) {
        router.replace({ name: "projects" });
      }

      return wrapper;
    }

    if (!params) return wrapper;

    wrapper.project = params;

    return finishWrapper(wrapper);
  };

  /** Same wrapper and side effects as `fetchProject`, without the API round trip; a project this user can't
   * read comes back null from Hasura, mirroring the REST read's non-200 case — leave for the project list. */
  const fetchProjectFromHasura = async (id: string) => {
    fetchProjectLoading.value = true;

    const empty: ProjectWrapperType = {
      project: {} as ProjectWrapperType["project"],
      models: [] as ProjectWrapperType["models"],
    };

    let wrapper: ProjectWrapperType | null = null;
    try {
      const result = await gaiupApolloClient.query<GetProjectSettingsQuery, GetProjectSettingsQueryVariables>({
        query: GetProjectSettingsDocument,
        fetchPolicy: "network-only",
        variables: { projectId: id },
      });
      wrapper = result.data ? toProjectWrapper(result.data) : null;
    } catch (queryError) {
      fetchProjectLoading.value = false;
      error.value = true;
      throw queryError;
    }

    if (!wrapper) {
      fetchProjectLoading.value = false;
      if (router) router.replace({ name: "projects" });
      return empty;
    }

    return finishWrapper(wrapper);
  };

  // Everything both reads do once they hold a project: derived fields, the ids the other queries key on, and
  // the transport rows, which have always come from Hasura.
  const finishWrapper = async (wrapper: ProjectWrapperType) => {
    // Get project request models
    if (!wrapper.project?.projectName) return wrapper;

    // Translate phases (mostly just for kicks - we need to do this somehow else in the future)
    wrapper.project.emissionCalculationAssumptions.emissionMetaData = {
      ...wrapper.project.emissionCalculationAssumptions.emissionMetaData,
      includedPhasesTranslated: BuildingProjectServices.includedPhasesConversion.getIncludedPhasesAsName(
        wrapper.project.emissionCalculationAssumptions.emissionMetaData.includedPhases
      ),
    };

    // Only fill from speckleData when the read didn't already provide models: the REST read's derived
    // ids are Speckle model ids, not `project_model.id` — the Hasura read's own rows carry the real one.
    if (wrapper.models.length === 0) {
      wrapper.models = [
        ...wrapper.project.speckleData.branches.map((model) => ({
          ...model,
          isActive: true,
        })),
        ...wrapper.project.speckleData.inactiveBranches.map((model) => ({
          ...model,
          isActive: false,
        })),
      ] as ModelType[];
    }

    // Set id
    setCurrentProjectId(wrapper.project.id);

    // Set query variables
    setQueryVariables(wrapper.project.id);

    // network-only, not Vue Apollo's useLazyQuery: load() only fires on the first call and returns
    // false silently after that, so a forced refresh would reuse stale transport data.
    let projectResult: Awaited<ReturnType<typeof gaiupApolloClient.query<GetProjectQuery, GetProjectQueryVariables>>>;
    try {
      projectResult = await gaiupApolloClient.query<GetProjectQuery, GetProjectQueryVariables>({
        query: GetProjectDocument,
        fetchPolicy: "network-only",
        variables: queryVariables.value,
      });
    } catch (queryError) {
      // Rejects rather than falling back to empty transportTypes — a swallowed failure here used to
      // leave Save believing an empty Transport section was the project's real state.
      fetchProjectLoading.value = false;
      error.value = true;
      throw queryError;
    }

    wrapper.project.transportTypes = toTransportTypes(projectResult.data);

    // Stop loading
    fetchProjectLoading.value = false;

    // Set has loaded (once)
    fetchProjectHasLoaded.value = true;

    return wrapper;
  };

  const fetchAdditionalProjectInfo = async (id: string) => {
    // Start loading
    fetchAdditionalLoading.value = true;

    // Define wrapper
    const wrapper: ProjectAdditionalInfoWrapperType = {
      userPermissions: [] as ProjectAdditionalInfoWrapperType["userPermissions"],
    };

    wrapper.userPermissions = await getProjectPermissions(id);

    // Set id
    setCurrentProjectAdditionalInfoId(id + "info");

    // Stop loading
    fetchAdditionalLoading.value = false;

    // Set has loaded (once)
    fetchAdditionalHasLoaded.value = true;

    return wrapper;
  };

  // In-flight refreshes keyed by project id — concurrent refreshes must share one fetch, or a second
  // setProjectWrapper detaches components still holding the first wrapper reference.
  let inflightProjectRefresh: { id: string; promise: Promise<void> } | null = null;
  let inflightAdditionalInfoRefresh: { id: string; promise: Promise<void> } | null = null;

  const refreshProjectWrapper = async (id: string, force?: boolean, source: ProjectReadSource = "rest") => {
    // Make sure we are not refreshing the same project
    if (id === getCurrentProjectId() && !force) return;

    // Join an already-running refresh for the same project
    if (inflightProjectRefresh?.id === id) return inflightProjectRefresh.promise;

    const refreshPromise = (async () => {
      setQueryVariables(undefined);

      // Fetch new project
      const newProjectWrapper = source === "hasura" ? await fetchProjectFromHasura(id) : await fetchProject(id);

      // Set new project if it exists
      if (!newProjectWrapper) return;
      localization.setCountryFromLongName(newProjectWrapper.project.buildingAddress?.country);
      setProjectWrapper(newProjectWrapper);
    })();

    inflightProjectRefresh = { id, promise: refreshPromise };
    try {
      await refreshPromise;
    } finally {
      inflightProjectRefresh = null;
    }
  };

  const refreshProjectAdditionalInfoWrapper = async (id: string, force?: boolean) => {
    // Make sure we are not refreshing the same project
    if (id + "info" === getCurrentProjectAdditionalInfoId() && !force) return;

    // Join an already-running refresh for the same project
    if (inflightAdditionalInfoRefresh?.id === id) return inflightAdditionalInfoRefresh.promise;

    const refreshPromise = (async () => {
      // Fetch new project
      const newProjectAdditionalInfoWrapper = await fetchAdditionalProjectInfo(id);

      // Set new project if it exists
      if (!newProjectAdditionalInfoWrapper) return;
      setProjectAdditionalInfoWrapper(newProjectAdditionalInfoWrapper);
    })();

    inflightAdditionalInfoRefresh = { id, promise: refreshPromise };
    try {
      await refreshPromise;
    } finally {
      inflightAdditionalInfoRefresh = null;
    }
  };

  const forceUpdateProjectWrapperAndAdditionalInfo = async (id: string, source: ProjectReadSource = "rest") => {
    await refreshProjectWrapper(id, true, source);
    await refreshProjectAdditionalInfoWrapper(id, true);
  };

  const reset = () => {
    setProjectWrapper({
      project: {} as ProjectWrapperType["project"],
      models: [] as ProjectWrapperType["models"],
    });

    setProjectAdditionalInfoWrapper({
      userPermissions: [] as ProjectAdditionalInfoWrapperType["userPermissions"],
    });

    setCurrentProjectId(undefined);
    setCurrentProjectAdditionalInfoId(undefined);

    setQueryVariables(undefined);

    setAddedTransportTypeIds([]);
  };

  // Methods for checking project state conditions like phases etc.

  const hasPhaseA4 = () => {
    // Transport phase
    return projectWrapper.value?.project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhasesTranslated?.includes(
      "A4"
    );
  };

  const hasPhaseA5 = () => {
    // Installation phase
    return projectWrapper.value?.project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhasesTranslated?.includes(
      "A5"
    );
  };

  const hasPhaseB6 = () => {
    // Operational Energy Use phase
    return projectWrapper.value?.project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhasesTranslated?.includes(
      "B6"
    );
  };

  // Utility

  const fetchProjectLoading = ref<boolean>(false);
  const fetchAdditionalLoading = ref<boolean>(false);
  const fetchProjectHasLoaded = ref<boolean>(false);
  const fetchAdditionalHasLoaded = ref<boolean>(false);
  const hasLoaded = ref<boolean>(false);
  const loading = ref<boolean>(true);
  const error = ref<boolean>(false);

  function getError() {
    return error.value;
  }

  function getLoading() {
    return loading.value;
  }

  function getHasLoaded() {
    return hasLoaded.value;
  }

  watch([fetchProjectLoading, fetchAdditionalLoading], ([newLoading1, newLoading2]) => {
    loading.value = newLoading1 || newLoading2;
  });

  watch([fetchProjectHasLoaded, fetchAdditionalHasLoaded], ([newHasLoaded1, newHasLoaded2]) => {
    hasLoaded.value = newHasLoaded1 && newHasLoaded2;
  });

  // Returns

  return {
    getProjectWrapper,
    getProjectAdditionalInfoWrapper,
    getTransportTypes,
    getAddedTransportTypeIds,
    setAddedTransportTypeIds,
    fetchProjectFromHasura,
    refreshProjectWrapper,
    refreshProjectAdditionalInfoWrapper,
    forceUpdateProjectWrapperAndAdditionalInfo,
    resetEmissionFactors,
    reset,
    insertProjectTransport,
    fetchProject,

    // Project state conditions
    hasPhaseA4,
    hasPhaseA5,
    hasPhaseB6,

    // Utility
    getLoading,
    getHasLoaded,
    getError,
  };
});

export default useProjectWrapper;

export type { ProjectWrapperType };
